import { noDataPlaceholder } from "../constants";
import EmployeeRepository from "../repositories/EmployeeRepository";
import {
    formatDate,
    sort,
    hasInErrorReason,
    normalizeDateOfBirth,
} from "../utils/helpers";
import { isOdsClaimInError } from "../utils/inErrorRecords";
import ClaimLeaveData from "./ClaimLeaveData";

class ClaimDetails {
    _formatHeader = (data) => {
        return {
            ClaimHistory: data.ClaimHistory,
            ClaimNumber: data.ClaimNumber,
            Coverage: data.Coverage,
            CurrentStatus: data.CurrentStatus,
            DateOfBirth: data.DateOfBirth,
            EmployeeID: data.EmployeeID,
            FirstName: data.FirstName,
            LastName: data.LastName,
            MiddleName: data.MiddleName,
            PolicyDiv: data.PolicyDiv,
            PolicyNo: data.PolicyNo,
            SSN: data.SSN,
        };
    };

    _formatEmployeeDetails = (data) => {
        return {
            SSN: data.SSN,
            FirstName: data.FirstName,
            MiddleName: data.MiddleName,
            LastName: data.LastName,
            DateOfBirth: normalizeDateOfBirth(data.DateOfBirth),
            EmployeeID: data.EmployeeID,
        };
    };

    _formatClaimLeaveData = (data) => {
        const header = data.Header[0] || {};
        const claimStatus = (data.ClaimStatus || [])[0] || {};

        return [
            {
                No: header.ClaimNumber || "",
                Group: header.PolicyDiv || "",
                Coverage: header.Coverage || "",
                ReceivedDate: claimStatus.ReceivedDate || null,
                Disability: claimStatus.DisabilityDate || null,
                CurrentStatus: header.CurrentStatus || "",
                CurrentStatusDate: claimStatus.ApprovalDate || null,
                Status: claimStatus.PaymentStatus || "",
                LastWorkDate: claimStatus.ReturntoWorkDatePT || null,
                StartDate: claimStatus.BenefitStartDate || null,
                EndDate: claimStatus.ApprovedThruDate || null,
                MaximumDuration: claimStatus.MaximumDuartionDate || null,
                LeaveReason: claimStatus.DiagnosticDescription || "",
                RTWDate: claimStatus.ReturntoWorkDateFT || null,
                RTWStatus: claimStatus.WorkRelated || "",
                StatusReason: claimStatus.StatusReason || "",
                isClaim: true,
                Historical: false,
                OrganizationId: header.OrganizationId,
                EmployeeID: header.EmployeeID || null,
            },
        ];
    };

    _checkObject = (objProperty) =>
        objProperty ? objProperty : noDataPlaceholder;

    _checkNullAndReplace = (objProperty, dependantObj, customPlaceholder) => {
        if (objProperty) {
            return objProperty;
        } else if (dependantObj) {
            return customPlaceholder;
        } else {
            return <span>{noDataPlaceholder}</span>;
        }
    };

    _formatClaimStatus = (data) => {
        return {
            RelatedLeaves:
                data.RelatedLeaves?.split(",") !== null &&
                Array.isArray(data.RelatedLeaves?.split(","))
                    ? data.RelatedLeaves?.split(",")
                    : [],
            Division: this._checkObject(data.Division),
            StatusReason: this._checkObject(data.StatusReason),
            ReceivedDate: data.ReceivedDate,
            PaymentStatus: this._checkObject(data.PaymentStatus),
            LastPaymentAmount: data.LastPaymentAmount,
            LastPaymentDate: data.LastPaymentDate,
            TotalPaymentAmount: data.TotalPaymentAmount,
            BaseCoverage: data.BaseCoverage,
            SupplementalCoverage: data.SupplementalCoverage,
            TotalCoverage: data.TotalCoverage,
            DisabilityDate: data.DisabilityDate,
            IncurredDate: data.IncurredDate,
            CauseOfDeath: this._checkNullAndReplace(
                data.CauseOfDeath,
                data.DateOfDeath,
                noDataPlaceholder
            ),
            RetireeIndicator: this._checkObject(data.RetireeIndicator),
            DateOfDeath: data.DateOfDeath,
            BenefitStartDate: data.BenefitStartDate,
            ApprovalDate: data.ApprovalDate,
            EligibilityGroup: this._checkObject(data.EligibilityGroup),
            MaximumDuartionDate: data.MaximumDuartionDate,
            ApprovedThruDate: data.ApprovedThruDate,
            Choice: this._checkObject(data.Choice),
            ReturntoWorkDatePT: data.ReturntoWorkDatePT,
            DaysPaid: data.DaysPaid,
            ReturntoWorkDateFT: data.ReturntoWorkDateFT,
            ClaimClosedDate: data.ClaimClosedDate,
            ClaimClosedReason: this._checkObject(data.ClaimClosedReason),
            MonthsPaid: data.MonthsPaid ? data.MonthsPaid : noDataPlaceholder,
            TotalPaidToDate: data.TotalPaidToDate,
            PaidThroughDate: data.PaidThroughDate,
            SSDIAwardDate: data.SSDIAwardDate,
            WorkRelated: data.WorkRelated
                ? data.WorkRelated
                : noDataPlaceholder,
            DisabilityBenefitsSpecialist: data.DisabilityBenefitsSpecialist
                ? data.DisabilityBenefitsSpecialist
                : noDataPlaceholder,
            Claimants: data.Claimants ? data.Claimants : noDataPlaceholder,
            InsuredRelationship: data.InsuredRelationship
                ? data.InsuredRelationship
                : noDataPlaceholder,
            LifeBenefitsSpecialist: data.LifeBenefitsSpecialist
                ? data.LifeBenefitsSpecialist
                : noDataPlaceholder,
            DiagnosticCode: data.DiagnosticCode
                ? data.DiagnosticCode
                : noDataPlaceholder,
            DiagnosticDescription: data.DiagnosticDescription
                ? data.DiagnosticDescription
                : noDataPlaceholder,
        };
    };

    format = (data) => {
        const list = {};

        list.Header = data.Header[0]
            ? this._formatHeader(data.Header[0])
            : null;

        list.EmployeeDetails = data.Header[0]
            ? this._formatEmployeeDetails(data.Header[0])
            : null;

        const validClaimStatus = (data.ClaimStatus || []).find(
            (status) =>
                !hasInErrorReason(
                    status.claimProcessingStage,
                    status.StatusReason,
                    status.ClaimClosedReason,
                    status.claimStatusReasonDesc,
                    status.claimStatusReasonCdDesc,
                    status.customClaimStatusReasonCdDesc
                )
        );

        list.ClaimStatus = validClaimStatus
            ? this._formatClaimStatus(validClaimStatus)
            : null;

        const payments = [];

        data.PaymentStatus.forEach((el) => {
            const payment = el;

            if (payment.PaymentDate !== null || payment.PaymentType !== null) {
                const paymentBreakdown = data.AmountStatus
                    ? data.AmountStatus.filter(
                          (a) => a.ClaimPaymentId === payment.ClaimPaymentId
                      )
                    : [];

                payments.push({
                    ...payment,
                    PaymentPeriod: `${formatDate(
                        payment.PaymentPeriodStartDate
                    )} - ${formatDate(payment.PaymentPeriodEndDate)}`,
                    PaymentAmountStatus:
                        EmployeeRepository.getClaimPaymentAmountStatus(
                            paymentBreakdown
                        ),
                });
            }
        });

        list.Payments = sort(payments, "PaymentDate", false);
        list.ClaimLeaveData = this._formatClaimLeaveData(data);
        return list;
    };

    formatODS = (data) => {
        const claim = data.claim || {};
        if (!claim.claimId || isOdsClaimInError(claim))
            return {
                Header: null,
            };

        const claimant = claim.claimantDetails || {};
        const coverage = claim.coverageDetail || {};
        const disability = claim.disabilityDetail || {};
        const paymentsSummary = claim.claimPaymentsSummary || {};
        const insuredDetails = claim.insuredDetails || {};
        const claimOwner = claim.claimOwner || {};

        const header = {
            ClaimHistory: claim.claimSystemCd === "NVLNK" ? "NL" : "FN",
            ClaimNumber: claim.claimId || noDataPlaceholder,
            Coverage: coverage.claimCoverageTypeCd || noDataPlaceholder,
            CurrentStatus: claim.derivedClaimStatusDesc || noDataPlaceholder,
            DateOfBirth: claimant.dateOfBirth || noDataPlaceholder,
            EmployeeID: claimant.employeeId || noDataPlaceholder,
            FirstName: claimant.firstName || noDataPlaceholder,
            LastName: claimant.lastName || noDataPlaceholder,
            MiddleName: "",
            PolicyDiv: coverage.divisionName || noDataPlaceholder,
            PolicyNo: coverage.groupAgreementID || noDataPlaceholder,
            SSN: claimant.taxIdentificationNumber || noDataPlaceholder,
            OrganizationId:
                claim.srcOrgDetails?.organizationId ||
                claim.srcOrgDetails?.orgId,
        };

        const claimStatus = {
            RelatedLeaves: claim.relatedLeaveIds
                ? claim.relatedLeaveIds.split(",").map((id) => id.trim())
                : [],
            Division: coverage.divisionName || noDataPlaceholder,
            StatusReason:
                claim.customClaimStatusReason ||
                claim.customClaimStatusReasonDesc ||
                noDataPlaceholder,
            ReceivedDate: claim.claimReceivedTs || null,
            PaymentStatus: claim.claimStatus || noDataPlaceholder,
            LastPaymentAmount: paymentsSummary.lastPaymentAmt || null,
            LastPaymentDate: paymentsSummary.lastPaymentDt || null,
            TotalPaymentAmount: paymentsSummary.totalPaidToDtAmt || null,
            BaseCoverage: claim.baseCoverageAmt || null,
            SupplementalCoverage: claim.supplementalCoverageAmt || null,
            TotalCoverage: claim.totalCoverageAmt || null,
            DisabilityDate: disability.disabilityBeginDt || null,
            IncurredDate: claim.incurredDt || null,
            CauseOfDeath: claim.causeOfDeath || null,
            RetireeIndicator: claim.retireeInd || noDataPlaceholder,
            DateOfDeath: claim.dateOfDeath || noDataPlaceholder,
            BenefitStartDate: claim.claimBenefitBeginDt || null,
            ApprovalDate: claim.claimStatusEffectiveDt || null,
            EligibilityGroup: coverage.eligibilityGroup || noDataPlaceholder,
            MaximumDuartionDate: claim.claimBenefitEndDt || null,
            ApprovedThruDate: claim.claimApprovedThruDt || null,
            Choice: coverage.choiceCd || noDataPlaceholder,
            ReturntoWorkDatePT: disability.partTimeRtwDt || null,
            DaysPaid: paymentsSummary.totalDaysPaidCount || noDataPlaceholder,
            ReturntoWorkDateFT: disability.fullTimeRtwDt || null,
            ClaimClosedDate: claim.claimClosedDt || null,
            ClaimClosedReason:
                claim.customClaimStatusReason || noDataPlaceholder,
            MonthsPaid:
                paymentsSummary.totalMonthPaidCount || noDataPlaceholder,
            TotalPaidToDate: paymentsSummary.totalPaidToDtAmt || null,
            PaidThroughDate: claim.claimPaidThruDt || null,
            SSDIAwardDate: null,
            WorkRelated: disability.workRelatedInd || noDataPlaceholder,
            DisabilityBenefitsSpecialist:
                claimOwner.personDisplayName || noDataPlaceholder,
            Claimants: claimant.claimantDisplayName || noDataPlaceholder,
            InsuredRelationship:
                insuredDetails.insuredRelationshipDesc || noDataPlaceholder,
            LifeBenefitsSpecialist:
                claimOwner.personDisplayName || noDataPlaceholder,
            DiagnosticCode: disability.primaryDiagnosisCd || noDataPlaceholder,
            DiagnosticDescription:
                disability.primaryDiagnosisDesc || noDataPlaceholder,
        };

        const payments = (claim.payments || []).map((payment) => ({
            ClaimPaymentId: payment.paymentId,
            PaymentDate: payment.paymentIssueDt || null,
            PaymentType: payment.paymentTransmitMethodDesc || null,
            PaymentStatus: payment.paymentStatus,
            PaymentAmount: payment.paymentAmt,
            PaymentPeriod: `${formatDate(payment.paymentPeriodBeginDt)} - ${formatDate(payment.paymentPeriodEndDt)}`,
            PaymentAmountStatus: (payment.paymentComponents || []).map(
                (comp) => ({
                    ClaimComponentId: comp.componentDescription,
                    ComponentDescription: comp.componentDescription,
                    ProcessedType: comp.processedType,
                    PaymentBeginDate: comp.paymentBeginDt,
                    PaymentEndDate: comp.paymentEndDt,
                    ComponentPaymentAmount: comp.componentPaymentAmt,
                })
            ),
        }));

        return {
            Header: header,
            ClaimStatus: claimStatus,
            Payments: sort(payments, "PaymentDate", false),
            EmployeeDetails: this._formatEmployeeDetails(header),
            ClaimLeaveData: this._formatClaimLeaveData({
                Header: [header],
                ClaimStatus: [claimStatus],
            }).map((row) => ({
                ...row,
                Group: ClaimLeaveData.formatClaimGroup(claim),
            })),
        };
    };
}

export default new ClaimDetails();
